/**
 * The Civil preview, at `/civil`: a house, from the roof down
 * (docs/foundations.md §8). Five small models, one decision each, the same
 * rule as Foundations: the learner sets something, the page checks it, and
 * the test pins where it passes and where it fails.
 *
 * Simplified on purpose and said so in every step: one allowable stress per
 * material in place of a design code's factors, uniform loads, perfect
 * supports. The numbers are ordinary textbook ones (timber about 7.5 MPa
 * allowable in bending, steel 165 MPa, E 11 and 210 GPa).
 */

export const CIVIL_STEP_IDS = ['rafters', 'lintel', 'footings', 'bracing', 'civil-ai'] as const;
export type CivilStepId = (typeof CIVIL_STEP_IDS)[number];

/* ── 1. Rafters under snow ─────────────────────────────────────────────── */

/** Roof tiles and battens plus snow, kPa (kN per square metre). */
export const ROOF_KPA = 1.5;
/** Rafter span, measured flat, metres. */
export const RAFTER_SPAN = 4.0;
/** Rafter width, mm (a standard 50 mm timber). */
const RAFTER_WIDTH = 50;
/** Allowable bending stress for structural softwood, MPa. */
const TIMBER_MPA = 7.5;
/** Timber the budget allows, cubic metres of rafter per metre of roof. */
export const RAFTER_BUDGET = 0.02;

export const SPACING_STOPS = [300, 400, 600] as const;
export const DEPTH_STOPS = [100, 150, 200, 250] as const;

export interface RafterResult {
  ok: boolean;
  stressMpa: number;
  allowMpa: number;
  /** m³ of timber per metre run of roof. */
  timber: number;
  strong: boolean;
  affordable: boolean;
}

export function rafters(spacingMm: number, depthMm: number): RafterResult {
  const w = ROOF_KPA * (spacingMm / 1000); // kN/m = N/mm
  const M = (w * (RAFTER_SPAN * 1000) ** 2) / 8; // N·mm
  const Z = (RAFTER_WIDTH * depthMm ** 2) / 6; // mm³
  const stressMpa = M / Z;
  const timber = ((RAFTER_WIDTH / 1000) * (depthMm / 1000)) / (spacingMm / 1000);
  const strong = stressMpa <= TIMBER_MPA;
  const affordable = timber <= RAFTER_BUDGET + 1e-12;
  return { ok: strong && affordable, stressMpa, allowMpa: TIMBER_MPA, timber, strong, affordable };
}

/* ── 2 and 5. A beam over an opening ───────────────────────────────────── */

/** The wall, floor and roof above an opening, kN per metre. */
export const LINTEL_KN_PER_M = 12;
/** The garage door the owner wants, metres. */
export const LINTEL_SPAN = 3.0;
/** Iris's case: a wider door. */
export const IRIS_SPAN = 3.6;

export const BEAM_IDS = ['timber-2x200', 'glulam-270', 'glulam-360', 'ipe-140', 'ipe-160', 'ipe-200'] as const;
export type BeamId = (typeof BEAM_IDS)[number];

export interface Beam {
  material: 'timber' | 'glulam' | 'steel';
  /** Elastic section modulus, mm³. */
  Z: number;
  /** Second moment of area, mm⁴. */
  I: number;
  /** Young's modulus, MPa. */
  E: number;
  /** Allowable bending stress, MPa. */
  allow: number;
  /** Depth, mm, for the drawing. */
  depth: number;
}

const rect = (b: number, d: number) => ({ Z: (b * d * d) / 6, I: (b * d ** 3) / 12, depth: d });

export const BEAMS: Record<BeamId, Beam> = {
  'timber-2x200': { material: 'timber', ...rect(100, 200), E: 11_000, allow: 7.5 },
  'glulam-270': { material: 'glulam', ...rect(90, 270), E: 11_500, allow: 11 },
  'glulam-360': { material: 'glulam', ...rect(90, 360), E: 11_500, allow: 11 },
  // European I-beams, published section properties.
  'ipe-140': { material: 'steel', Z: 77_300, I: 5_410_000, E: 210_000, allow: 165, depth: 140 },
  'ipe-160': { material: 'steel', Z: 109_000, I: 8_690_000, E: 210_000, allow: 165, depth: 160 },
  'ipe-200': { material: 'steel', Z: 194_000, I: 19_430_000, E: 210_000, allow: 165, depth: 200 },
};

export interface LintelResult {
  ok: boolean;
  stressMpa: number;
  allowMpa: number;
  sagMm: number;
  limitMm: number;
  strong: boolean;
  stiff: boolean;
}

/** Simply supported, uniform load: M = wL²/8, δ = 5wL⁴/384EI, sag limit L/360. */
export function lintel(beam: BeamId, span: number): LintelResult {
  const b = BEAMS[beam];
  const w = LINTEL_KN_PER_M; // N/mm
  const L = span * 1000;
  const stressMpa = (w * L * L) / 8 / b.Z;
  const sagMm = (5 * w * L ** 4) / (384 * b.E * b.I);
  const limitMm = L / 360;
  const strong = stressMpa <= b.allow;
  const stiff = sagMm <= limitMm;
  return { ok: strong && stiff, stressMpa, allowMpa: b.allow, sagMm, limitMm, strong, stiff };
}

/** What Iris proposes for the wider door. */
export const IRIS_BEAM: BeamId = 'ipe-140';

/* ── 3. Footings on two soils ──────────────────────────────────────────── */

/** The wall's load on its footing, kN per metre. */
export const WALL_KN_PER_M = 60;
/** Concrete, kN/m³, and the footing's thickness, m. */
const CONCRETE = 24;
const FOOTING_THICK = 0.5;
/** What the soil survey says each end can safely carry, kPa. */
export const SOIL_KPA = { front: 150, back: 75 } as const;
export const WIDTH_STOPS = [0.3, 0.4, 0.45, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0, 1.1, 1.2] as const;

export interface FootingResult {
  ok: boolean;
  pressureKpa: number;
  allowKpa: number;
}

/** Pressure under a strip footing: the wall plus the footing's own weight, over its width. */
export function footing(widthM: number, allowKpa: number): FootingResult {
  const pressureKpa = (WALL_KN_PER_M + CONCRETE * widthM * FOOTING_THICK) / widthM;
  return { ok: pressureKpa <= allowKpa, pressureKpa, allowKpa };
}

/* ── 4. Bracing against the wind ───────────────────────────────────────── */

/** Wind on the gable end, kPa, and the wall it pushes on, m. */
export const WIND_KPA = 0.8;
const GABLE_W = 8;
const GABLE_H = 6;
/** What one braced panel resists, kN. */
export const PANEL_KN = 5;
/** Slots along the side wall, each 1.2 m. */
export const SLOTS = 8;
/** Windows the owner asked for. */
export const WINDOWS_WANTED = 3;

export type Slot = 'plain' | 'brace' | 'window';

/** Each side wall takes half the wind on the gable. */
export function windKn(): number {
  return (WIND_KPA * GABLE_W * GABLE_H) / 2;
}

export interface BracingResult {
  ok: boolean;
  demandKn: number;
  capacityKn: number;
  windows: number;
  /** Braced at both corners, where the racking force arrives. */
  ends: boolean;
  broken: 'capacity' | 'ends' | 'windows' | null;
}

export function bracing(slots: readonly Slot[]): BracingResult {
  const braces = slots.filter((s) => s === 'brace').length;
  const windows = slots.filter((s) => s === 'window').length;
  const demandKn = windKn();
  const capacityKn = braces * PANEL_KN;
  const ends = slots[0] === 'brace' && slots[slots.length - 1] === 'brace';
  const broken = capacityKn < demandKn ? 'capacity' : !ends ? 'ends' : windows < WINDOWS_WANTED ? 'windows' : null;
  return { ok: broken === null, demandKn, capacityKn, windows, ends, broken };
}

export const EMPTY_WALL: readonly Slot[] = Array.from({ length: SLOTS }, () => 'plain' as Slot);
